import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { toast } from "react-toastify";
import SelectField from "../ui/SelectField";

const formatos = [
  { value: "carta", label: "Hoja tamaño carta (múltiples etiquetas)" },
  { value: "individual", label: "Etiquetas individuales" },
];

const ModalEtiquetasMasivas = ({ onClose }) => {
  const [clientes, setClientes] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState("");
  const [selectedSede, setSelectedSede] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [selectedEquipos, setSelectedEquipos] = useState([]);
  const [formato, setFormato] = useState("carta");
  const [isLoading, setIsLoading] = useState(false);

  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const seleccionarTodosEquipos = () => {
    setSelectedEquipos(equipos);
  };

  const limpiarEquipos = () => {
    setSelectedEquipos([]);
  };

  useEffect(() => {
    axios
      .get(`${API}/clientes`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setClientes(res.data))
      .catch(() => toast.error("Error al cargar clientes"));
  }, [API, token]);

  useEffect(() => {
    if (!selectedCliente) {
      setSedes([]);
      setSelectedSede("");
      setAreas([]);
      setSelectedArea("");
      setEquipos([]);
      setSelectedEquipos([]);
      return;
    }

    setSelectedSede("");
    setSelectedArea("");
    setEquipos([]);
    setSelectedEquipos([]);

    axios
      .get(`${API}/sedes/${selectedCliente}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setSedes(res.data || []))
      .catch(() => toast.error("Error al cargar sedes"));
  }, [selectedCliente, API, token]);

  useEffect(() => {
    if (!selectedCliente) {
      setAreas([]);
      setSelectedArea("");
      setEquipos([]);
      setSelectedEquipos([]);
      return;
    }

    axios
      .get(
        `${API}/areas/con_equipos?cliente=${selectedCliente}&sede=${
          selectedSede || "all"
        }`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then((res) => setAreas(res.data))
      .catch(() => toast.error("Error al cargar áreas"));
  }, [selectedCliente, selectedSede, API, token]);

  useEffect(() => {
    setSelectedArea("");
    setEquipos([]);
    setSelectedEquipos([]);
  }, [selectedSede]);

  useEffect(() => {
    if (!selectedArea) {
      setEquipos([]);
      setSelectedEquipos([]);
      return;
    }

    const sedeQuery = selectedSede ? `&sede=${selectedSede}` : "";
    axios
      .get(`${API}/equipos?area=${selectedArea}${sedeQuery}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setEquipos(res.data))
      .catch(() => toast.error("Error al cargar equipos"));
  }, [selectedArea, selectedSede, API, token]);

  const validar = () => {
    if (!selectedCliente) return toast.warning("Seleccione un cliente");
    if (!selectedArea) return toast.warning("Seleccione un área");
    if (!selectedEquipos.length)
      return toast.warning("Seleccione al menos un equipo");
    return true;
  };

  const handleGenerarReporte = async () => {
    if (!validar()) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("clienteId", selectedCliente);
      if (selectedSede) params.append("sedeId", selectedSede);
      params.append("areaId", selectedArea);
      selectedEquipos
        .map((equipo) => equipo?._id ?? equipo)
        .filter(Boolean)
        .forEach((equipoId) => params.append("equipos", equipoId));
      params.append("formato", formato);

      const res = await axios.get(`${API}/reportes/etiquetas?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "etiquetas_inventario.pdf";
      link.click();

      toast.success("Etiquetas generadas correctamente ✅");
      onClose();
    } catch (err) {
      toast.error("Error al generar las etiquetas");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            Imprimir etiquetas de inventario
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            <SelectField
              label="Cliente"
              name="cliente"
              value={selectedCliente}
              options={clientes}
              valueKey="_id"
              displayFn={(c) => c.name}
              placeholder="Seleccione cliente"
              onChange={(e) => setSelectedCliente(e.target.value)}
            />

            <SelectField
              label="Sede"
              name="sede"
              value={selectedSede}
              options={sedes}
              valueKey="_id"
              displayFn={(s) => s.name}
              placeholder={
                selectedCliente ? "Todas las sedes" : "Seleccione un cliente"
              }
              onChange={(e) => setSelectedSede(e.target.value)}
            />

            <SelectField
              label="Área"
              name="area"
              value={selectedArea}
              options={areas}
              valueKey="_id"
              displayFn={(a) =>
                a.grupo?.name ? `${a.grupo.name} - ${a.name}` : a.name
              }
              placeholder="Seleccione área"
              onChange={(e) => setSelectedArea(e.target.value)}
            />

            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium">Equipos</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={seleccionarTodosEquipos}
                    disabled={!equipos.length}
                    className="text-xs text-blue-600 hover:underline disabled:text-gray-400"
                  >
                    Seleccionar todos
                  </button>
                  <button
                    type="button"
                    onClick={limpiarEquipos}
                    disabled={!selectedEquipos.length}
                    className="text-xs text-red-500 hover:underline disabled:text-gray-400"
                  >
                    Limpiar
                  </button>
                </div>
              </div>

              <SelectField
                name="equipos"
                value={
                  Array.isArray(selectedEquipos)
                    ? selectedEquipos.map((e) => e._id)
                    : []
                }
                options={equipos}
                valueKey="_id"
                displayFn={(e) =>
                  `${e.marca?.name} ${e.modelo?.name} [${e.codigo}]`
                }
                placeholder="Seleccione equipos"
                isMulti
                onChange={(e) => setSelectedEquipos(e.target.value)}
              />

              {equipos.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {selectedEquipos.length} de {equipos.length} equipos
                  seleccionados
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Formato de impresión
              </label>
              <select
                name="formato"
                value={formato}
                onChange={(e) => setFormato(e.target.value)}
                className="w-full border rounded p-2"
              >
                {formatos.map((opcion) => (
                  <option key={opcion.value} value={opcion.value}>
                    {opcion.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleGenerarReporte}
            disabled={isLoading || !equipos.length}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            {isLoading ? "Generando..." : "Generar etiquetas"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ModalEtiquetasMasivas;
